import { Promise } from 'rsvp';
import { capitalize } from '@ember/string';
import { task } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import { computed, action } from '@ember/object';
import { readOnly } from '@ember/object/computed';

import BaseAnalytics from './base-analytics';
import { absoluteStoryhunterUrl } from "../../utils/url";
import { BILLABLE_RECORD } from "../../models/billable-record";

const DATE = 'dueAt';
const COST = 'amount';
const INVOICE_NUMBER = 'id';

const COST_CODES = 'costCodes';
const STORY_CODES = 'storyCodes';

const DESCRIPTION = 'description';
const CURRENT_STATUS = 'currentStatus';

const DUE = 'Due';
const PAID = 'Paid';
const OVERDUE = 'Overdue';

const FACTOID_DUE = 'Due';
const FACTOID_PAID = 'Paid';
const FACTOID_OVERDUE = 'Overdue';

const PAYMENT_STATUS_OPTIONS = [
  OVERDUE,
  DUE,
  PAID,
];

export default BaseAnalytics.extend({
  /**
   * Register services
   */
  shInvoiceCommandService : service('commands/sh-invoice-commands'),
  session: service(),
  store: service(),
  errorTracker: service('error-tracker'),

  currentUser: readOnly('session.currentUser'),

  ////////////
  // CONFIG //
  ////////////
  title: 'Invoices',
  factoidType: 'invoice',
  annual: false,
  hideGraph: true,
  allowTableDownload: false,

  /**
   * List of all invoices.  We will add to this last as we page from the server.
   *
   * @type {Array}
   */
  allInvoices: [],

  /**
   * @inheritDoc
   */
  _isSupportsPaging: true,

  hasMorePages: computed('_currentPagination.{currentPage,totalPages}', function() {
    return this._currentPagination ? this._currentPagination.currentPage < this._currentPagination.totalPages : false;
  }),

  data: computed({
    set(_key, value) {
      return value.map(shInvoice => {
        if (shInvoice.isPaid) {
          shInvoice.set(CURRENT_STATUS, FACTOID_PAID);
          return shInvoice;
        }

        let overDueAt = shInvoice.overdueAt;
        if (overDueAt && new Date().getTime() > overDueAt.getTime()) {
          shInvoice.set(CURRENT_STATUS, FACTOID_OVERDUE);
          return shInvoice;
        }

        if (shInvoice.type === BILLABLE_RECORD.TYPES.PROJECT_ADVANCE) {
          let description = shInvoice.get(DESCRIPTION);
          shInvoice.set(DESCRIPTION, `${description} (Advance)`);
        }

        shInvoice.set(CURRENT_STATUS, FACTOID_DUE);
        return shInvoice;
      });
    }
  }),

  ///////////
  // TABLE //
  ///////////
  tableColumns: computed('usesCostCodes', function() {
    const usesCostCodes = this.usesCostCodes;
    const formatMoney = this.formatMoney.bind(this);
    const formatDate = this.formatDate.bind(this);
    const formatArray = this.formatArray.bind(this);
    let columns = [
      {
        label: 'Invoice #',
        param: INVOICE_NUMBER,
        display: id => `#${id}`,
        sortFunction: (a, b) => parseInt(b) - parseInt(a)
      },
      {
        label: 'Date',
        param: DATE,
        display: formatDate,
        sortDescending: true,
        defaultSort: true,
      },
      {
        label: 'Cost Codes',
        param: COST_CODES,
        display: formatArray
      },
      {
        label: 'Story Codes',
        param: STORY_CODES,
        display: formatArray
      },
      {
        label: 'Project #',
        param: 'id',
        display: (invoiceId) => this._displayProjectLinks(invoiceId),
        isAllowHtml: true,
        wide: true,
      },
      {
        label: 'Amount ($USD)',
        param: COST,
        display: formatMoney
      },
      {
        label: 'Payment Status',
        param: CURRENT_STATUS,
        display: status => capitalize(String(status))
      },
      {
        param: INVOICE_NUMBER,
        excludeFromCSV: true,
        icon: 'file_download',
        action: id => this.downloadPDF(id)

      }
    ];

    if (!usesCostCodes) {
      columns = columns.filter(column => {
        return (column.param !== COST_CODES && column.param !== STORY_CODES);
      });
    }

    return columns;
  }),

  allCostCodes: computed('data.[]', function() {
    return this._allCodes(COST_CODES);
  }),

  allStoryCodes: computed('data.[]', function() {
    return this._allCodes(STORY_CODES);
  }),

  /**
   * Display project links for an invoice.  Or get all projects links of all child invoices if it is batched.
   *
   * @param {String} invoiceId
   * @return {string}       A list of html representing the project links for this invoice.
   * @private
   */
  _displayProjectLinks(invoiceId) {
    let shInvoice = this.store.peekRecord('shInvoice', invoiceId),
      applicationLinks = [];

    if (shInvoice.childShInvoices.length) {
      shInvoice.childShInvoices.forEach(childShInvoice => {
        if (childShInvoice.application) {
          applicationLinks.push(`<a target="_blank" href="/project/${childShInvoice.application.id}">${childShInvoice.application.id}</a>`);
        }
      });

      return applicationLinks.join(' ');
    }

    return `<a target="_blank" href="/project/${shInvoice.application.id}">${shInvoice.application.id}</a>`;
  },

  _allCodes(costCodeType) {
    const usesCostCodes = this.usesCostCodes;
    if (!usesCostCodes) {
      return [];
    }
    const data = this.data;
    const codes = data.reduce((codes, shInvoice) => {
      let invoiceCostCodes = shInvoice[costCodeType];
      if (!Array.isArray(shInvoice[costCodeType])) {
        invoiceCostCodes = Object.values(invoiceCostCodes);
      }

      invoiceCostCodes.forEach(code => {
        if (!code) {
          return;
        }

        codes.add(code);
      });

      return codes;
    }, new Set());

    return Array.from(codes).sort();
  },

  tableFilters: computed('allCostCodes', 'allStoryCodes', 'usesCostCodes', function() {
    const usesCostCodes = this.usesCostCodes;

    let costCodesOptions = [];
    costCodesOptions.push({
      title: 'Any Cost Code',
      value: null,
    });

    this.allCostCodes.forEach(costCode => {
      const selected = this.activeFilters.get('Cost Codes')?.[1] === costCode;

      costCodesOptions.push({
        title: costCode,
        value: costCode,
        selected,
      });
    });

    let storyCodeOptions = [];
    storyCodeOptions.push({
      title: 'Any Story Code',
      value: null,
    });

    this.allStoryCodes.forEach(storyCode => {
      const selected = this.activeFilters.get('Story Codes')?.[1] === storyCode;

      storyCodeOptions.push({
        title: storyCode,
        value: storyCode,
        selected,
      });
    });

    const paymentStatusOptions = [{
      title: 'Any Payment Status',
      value: null,
      selected: this.activeFilters.get('Payment Status')?.[1] === null,
    }];

    PAYMENT_STATUS_OPTIONS.forEach(status => {
      const selected = this.activeFilters.get('Payment Status')?.[1] === status;

      paymentStatusOptions.push({
        title: status,
        value: status,
        selected
      });
    })

    let filters = [
      {
        label: 'Payment Status',
        options: paymentStatusOptions,
        filter: (status, invoice) => {
          const invoiceStatus = invoice.get(CURRENT_STATUS);
          switch (status) {
            case PAID:
              return invoiceStatus === PAID;
            case DUE:
              return (invoiceStatus === DUE) || (invoiceStatus === OVERDUE);
            case OVERDUE:
              return invoiceStatus === OVERDUE;
          }

          return true;
        }
      },
      {
        label: 'Cost Codes',
        options: costCodesOptions,
        filter: (code, invoice) => {
          if (!code) {
            return true;
          }

          return invoice.get(COST_CODES).find(costCode => costCode === code);
        }
      },
      {
        label: 'Story Codes',
        options: storyCodeOptions,
        filter: (code, invoice) => {
          if (!code) {
            return true;
          }

          return invoice.get(STORY_CODES).find(storyCode => storyCode === code);
        }
      }
    ];
    if (!usesCostCodes) {
      filters = filters.filter(filter => {
        return (filter.label !== 'Cost Codes' && filter.label !== 'Story Codes');
      });
    }

    return filters;
  }),

  //////////////
  // FACTOIDS //
  //////////////
  factoidLayout: computed('media.isSearchMobile', function() {
    const mobile = this.get('media.isSearchMobile');
    if (mobile) {
      return [
        [{title: FACTOID_OVERDUE, color: 'red'}, {title: FACTOID_DUE}],
        [{title: FACTOID_PAID, color: 'green'}]
      ];
    }

    return [
      [{title: FACTOID_OVERDUE, color: 'red'}, {title: FACTOID_DUE}, {title: FACTOID_PAID, color: 'green'}]
    ];
  }),

  loadInvoicesTask: task(function *() {
    const companyId = this.currentUser.company.id;

    try {
      const response = yield this.shInvoiceCommandService.loadShInvoices(
        this._currentPage,
        this._pageSize,
        {
          company_id: companyId,
          due: '1'
        },
        [
          'application',
          'sh-invoice-line-items.application',
          'child-sh-invoices.sh-invoice-line-items.application'
        ],
        '',
        true
      );

      const newAllInvoices = this.allInvoices.concat(response.shInvoices);
      this.setProperties({
        allInvoices: newAllInvoices,
        _currentPagination: response.pagination,
        _currentPage: this._currentPage + 1,
        data: [...newAllInvoices],
      });

      return response.shInvoices;
    } catch (error) {
      this.errorTracker.error('Failed to load company invoices');
    }
  }).drop(),

  /////////////
  // METHODS //
  /////////////
  async queryData() {
    return await this.loadInvoicesTask.perform();
  },

  peekData() {
    return this.store.peekAll('sh-invoice');
  },

  downloadPDF: action(function(id) {
    const shInvoice = this.store.peekRecord('sh-invoice', id);
    if (!shInvoice) {
      return false;
    }

    window.open(absoluteStoryhunterUrl(`/sh-invoices/${shInvoice.id}/pdf`));
  }),

  actions: {
    onInfiniteScrollTrigger() {
      if (this.hasMorePages) {
        this.loadInvoicesTask.perform();
      }
    },
  }
});
